/* eslint-disable max-lines */
import { autoUpdate, computePosition, offset, shift } from '@floating-ui/dom';
import { PluginKey } from '@tiptap/pm/state';
import { type Editor, Extension, ReactRenderer } from '@tiptap/react';
import Suggestion, {
  type SuggestionKeyDownProps,
  type SuggestionProps,
} from '@tiptap/suggestion';
import { CommandList } from './command-list';
import { type Command, type CommandListProps, type Group } from './types';

import { isEditorLive } from '@/components/tiptap/shared/safe-editor';

const extensionName = 'slash-commands';

interface PopupInstance {
  element: HTMLElement;
  isShown: boolean;
  show: () => void;
  hide: () => void;
  setReferenceRect: (rect: DOMRect | (() => DOMRect)) => void;
  setContent: (content: HTMLElement) => void;
  destroy: () => void;
}

export interface SlashCommandStorage {
  rect:
    | DOMRect
    | {
        width: number;
        height: number;
        left: number;
        top: number;
        right: number;
        bottom: number;
      };
  popup?: PopupInstance;
  // Suppresses the suggestion popup from re-opening immediately after a
  // command is executed. Some commands (e.g. protocols) insert text starting
  // with "/" which re-triggers the suggestion plugin. Set in the command
  // callback and cleared after the next animation frame.
  suppressNextOpen: boolean;
}

interface CommandListRef {
  onKeyDown: (props: SuggestionKeyDownProps) => boolean;
}

interface ReferenceRectParams {
  editor: Editor;
  clientRect: (() => DOMRect | null) | null | undefined;
  extensionStorage: SlashCommandStorage;
  componentElement: HTMLElement | null;
}

export function createGetReferenceClientRect({
  editor,
  clientRect,
  extensionStorage,
  componentElement,
}: ReferenceRectParams): () => DOMRect {
  return () => {
    // A leaked autoUpdate listener can call this after the editor is
    // destroyed; clientRect() would then throw via the editor view proxy.
    if (!isEditorLive(editor, 'slash-command.referenceRect') || !clientRect) {
      return new DOMRect(
        extensionStorage.rect.left,
        extensionStorage.rect.top,
        extensionStorage.rect.width,
        extensionStorage.rect.height
      );
    }

    const rect = clientRect();

    if (!rect) {
      return new DOMRect(
        extensionStorage.rect.left,
        extensionStorage.rect.top,
        extensionStorage.rect.width,
        extensionStorage.rect.height
      );
    }

    let yPos = rect.y;
    if (
      componentElement instanceof HTMLElement &&
      rect.top + componentElement.offsetHeight + 40 > window.innerHeight
    ) {
      const diff = rect.top + componentElement.offsetHeight - window.innerHeight + 40;
      yPos = rect.y - diff;
    }

    return new DOMRect(rect.x, yPos, rect.width, rect.height);
  };
}

export interface SlashCommandOptions {
  getGroups: (editor: Editor) => Group[];
  char?: string;
  allowSpaces?: boolean;
}

function createPopup(): PopupInstance {
  const element = document.createElement('div');
  element.style.position = 'fixed';
  element.style.maxWidth = '16rem';
  element.style.zIndex = '9999';
  element.style.display = 'none';
  document.body.appendChild(element);

  let referenceRect: DOMRect | (() => DOMRect) = new DOMRect(0, 0, 0, 0);
  let cleanup: (() => void) | undefined;
  let isShown = false;

  const updatePosition = async () => {
    const rect = typeof referenceRect === 'function' ? referenceRect() : referenceRect;

    // Create a virtual element for the reference
    const virtualElement = {
      getBoundingClientRect: () => rect,
    };

    const { x, y } = await computePosition(virtualElement, element, {
      placement: 'bottom-start',
      strategy: 'fixed',
      middleware: [offset({ mainAxis: 8, crossAxis: 16 }), shift({ padding: 8 })],
    });

    element.style.left = `${x}px`;
    element.style.top = `${y}px`;
  };

  return {
    element,
    get isShown() {
      return isShown;
    },
    show: () => {
      element.style.display = 'block';
      isShown = true;
      void updatePosition();
    },
    hide: () => {
      element.style.display = 'none';
      isShown = false;
      cleanup?.();
      cleanup = undefined;
    },
    setReferenceRect: (rect: DOMRect | (() => DOMRect)) => {
      referenceRect = rect;

      // Clean up previous autoUpdate
      cleanup?.();

      // Set up autoUpdate for automatic position updates
      const virtualElement = {
        getBoundingClientRect: () => (typeof rect === 'function' ? rect() : rect),
      };

      cleanup = autoUpdate(virtualElement, element, () => {
        void updatePosition();
      });
    },
    setContent: (content: HTMLElement) => {
      element.innerHTML = '';
      element.appendChild(content);
      void updatePosition();
    },
    destroy: () => {
      cleanup?.();
      cleanup = undefined;
      isShown = false;
      element.remove();
    },
  };
}

export function createSlashCommand(options: SlashCommandOptions) {
  const { getGroups, char = '/', allowSpaces = true } = options;

  return Extension.create<SlashCommandOptions, SlashCommandStorage>({
    name: extensionName,

    onCreate() {
      this.storage.popup = createPopup();
    },

    onDestroy() {
      this.storage.popup?.destroy();
      this.storage.popup = undefined;
    },

    addStorage(): SlashCommandStorage {
      return {
        rect: {
          width: 0,
          height: 0,
          left: 0,
          top: 0,
          right: 0,
          bottom: 0,
        },
        suppressNextOpen: false,
      };
    },

    addProseMirrorPlugins() {
      const extensionStorage = this.storage;

      return [
        Suggestion<Group>({
          editor: this.editor,
          pluginKey: new PluginKey(extensionName),
          char,
          allowSpaces,

          items: ({ query }: { query: string }) => {
            const groups = getGroups(this.editor);
            const withFilteredCommands = groups.map((group) => ({
              ...group,
              commands: group.commands
                .filter((item) => {
                  const normalizeText = (text: string | undefined) => {
                    return text?.toLowerCase().trim() ?? '';
                  };
                  const queryNormalized = normalizeText(query);
                  const labelNormalized = normalizeText(item.label);
                  const groupNameNormalized = normalizeText(group.name);
                  const descriptionNormalized = normalizeText(item.description);
                  return (
                    labelNormalized?.includes(queryNormalized) ||
                    groupNameNormalized?.includes(queryNormalized) ||
                    descriptionNormalized?.includes(queryNormalized)
                  );
                })
                .filter((command) =>
                  command.shouldBeHidden ? !command.shouldBeHidden(this.editor) : true
                ),
            }));

            const withoutEmptyGroups = withFilteredCommands.filter((group) => {
              if (group.commands.length > 0) {
                return true;
              }

              return false;
            });

            const withEnabledSettings = withoutEmptyGroups.map((group) => ({
              ...group,
              commands: group.commands.map((command) => ({
                ...command,
                isEnabled: true,
              })),
            }));

            return withEnabledSettings;
          },

          command: ({ editor, props }: { editor: Editor; props: Command }) => {
            const { view, state } = editor;
            const { $from } = view.state.selection;

            const end = $from.pos;
            const textUntilPos = $from.parent.textContent.slice(0, $from.parentOffset);
            const lastSlashPos = textUntilPos.lastIndexOf(char);
            if (lastSlashPos === -1) {
              return;
            }
            const from = $from.start() + lastSlashPos;

            const tr = state.tr.deleteRange(from, end);
            view.dispatch(tr);

            // Suppress re-opening if the action inserts text starting with "/"
            extensionStorage.suppressNextOpen = true;
            requestAnimationFrame(() => {
              extensionStorage.suppressNextOpen = false;
            });

            props.action(editor);
            view.focus();
          },

          render: () => {
            let component: ReactRenderer<CommandListRef, CommandListProps> | undefined;

            // One scroll handler for the whole suggestion session; onUpdate
            // swaps the rect getter it reads instead of stacking listeners.
            let currentGetRect: (() => DOMRect) | null = null;
            let scrollHandler: (() => void) | null = null;
            // Captured at onStart so onExit unregisters from the very element
            // it registered on, without re-resolving it through the editor
            // view while the editor is being torn down.
            let scrollContainer: HTMLElement | null = null;
            // The suggestion plugin keeps calling onUpdate/onKeyDown for a
            // session whose onStart was suppressed; without this flag they
            // would service stale state from a prior session. onExit reads it
            // too, so a suppressed session tears nothing down.
            let sessionActive = false;

            return {
              onStart: (props: SuggestionProps<Group, Command>) => {
                // items() is awaited before this runs, so the editor can be
                // destroyed inside that gap. Reading view.dom below would then
                // throw through the destroyed-view proxy — unhandled, since
                // nothing awaits this callback — and strand a half-built
                // session whose renderer no onExit would ever destroy.
                if (!isEditorLive(props.editor, 'slash-command.onStart')) {
                  sessionActive = false;
                  return;
                }

                // Skip re-opening if a command just executed (e.g. protocol
                // insertion that starts with "/" re-triggers the suggestion).
                if (extensionStorage.suppressNextOpen) {
                  extensionStorage.suppressNextOpen = false;
                  sessionActive = false;
                  return;
                }
                sessionActive = true;

                component = new ReactRenderer<CommandListRef, CommandListProps>(
                  CommandList,
                  {
                    props,
                    editor: props.editor,
                  }
                );

                const { view } = props.editor;

                currentGetRect = createGetReferenceClientRect({
                  editor: props.editor,
                  clientRect: props.clientRect,
                  extensionStorage,
                  componentElement: component?.element ?? null,
                });

                scrollHandler = () => {
                  if (currentGetRect) {
                    extensionStorage.popup?.setReferenceRect(currentGetRect);
                  }
                };

                scrollContainer = view.dom.parentElement;
                scrollContainer?.addEventListener('scroll', scrollHandler);

                extensionStorage.popup?.setContent(component.element);
                extensionStorage.popup?.setReferenceRect(currentGetRect);
                extensionStorage.popup?.show();
              },

              onUpdate(props: SuggestionProps<Group, Command>) {
                if (!sessionActive) {
                  return;
                }

                component?.updateProps(props);

                const getReferenceClientRect = createGetReferenceClientRect({
                  editor: props.editor,
                  clientRect: props.clientRect,
                  extensionStorage,
                  componentElement: component?.element ?? null,
                });
                currentGetRect = getReferenceClientRect;

                extensionStorage.rect = props.clientRect
                  ? getReferenceClientRect()
                  : {
                      width: 0,
                      height: 0,
                      left: 0,
                      top: 0,
                      right: 0,
                      bottom: 0,
                    };
                extensionStorage.popup?.setReferenceRect(getReferenceClientRect);
              },

              onKeyDown(props: SuggestionKeyDownProps) {
                if (!sessionActive) {
                  return false;
                }

                if (props.event.key === 'Escape') {
                  extensionStorage.popup?.hide();

                  return true;
                }

                if (!extensionStorage.popup?.isShown) {
                  extensionStorage.popup?.show();
                }

                return component?.ref?.onKeyDown(props) ?? false;
              },

              onExit() {
                if (!sessionActive) {
                  return;
                }
                sessionActive = false;

                extensionStorage.popup?.hide();
                if (scrollHandler) {
                  scrollContainer?.removeEventListener('scroll', scrollHandler);
                }
                scrollContainer = null;
                scrollHandler = null;
                currentGetRect = null;
                component?.destroy();
                component = undefined;
              },
            };
          },
        }),
      ];
    },
  });
}
